/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { cn } from '@/lib/utils';
import type { RefinementProps } from '../types';

export default function RefineSize({
    values,
    attributeId,
    isFilterSelected,
    toggleFilter,
}: RefinementProps): ReactElement {
    return (
        <ul className="mt-2 flex flex-wrap gap-2">
            {values.map((value) => {
                const isSelected = isFilterSelected(attributeId, value.value);

                return (
                    <li key={`${attributeId}:${value.value}`}>
                        <button
                            type="button"
                            onClick={() => toggleFilter(attributeId, value.value)}
                            aria-pressed={isSelected}
                            className={cn(
                                'inline-flex min-w-[2.5rem] cursor-pointer items-center justify-center rounded-xs px-2 py-1 text-sm leading-5 text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                isSelected ? 'bg-secondary font-semibold' : 'bg-transparent'
                            )}>
                            {value.label || value.value}
                            {value.hitCount !== undefined && (
                                <span className="ml-1 tabular-nums text-muted-foreground">({value.hitCount})</span>
                            )}
                        </button>
                    </li>
                );
            })}
        </ul>
    );
}
